import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { 
  MoveVertical, ArrowLeft, Plus, Trash2, Edit, RefreshCw, 
  Settings, CheckCircle, XCircle, Save, Cpu, Network, Database, History
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ConfiguracaoElevadores = () => {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availableRegisters, setAvailableRegisters] = useState([]);
  const [automatoTipos, setAutomatoTipos] = useState([]);
  const [registosEntradas, setRegistosEntradas] = useState([]);
  const [registosSaidas, setRegistosSaidas] = useState([]);
  const [registosEstado, setRegistosEstado] = useState([]);
  
  // Device form
  const [deviceForm, setDeviceForm] = useState({
    local: '',
    descricao: '',
    equipamento_id: '',
    automato_tipo: 'schneider_tm221',
    automato_ip: '',
    automato_port: 502,
    unit_id: 1,
    registos_config: {}
  });
  const [editingDevice, setEditingDevice] = useState(null);
  const [showDeviceDialog, setShowDeviceDialog] = useState(false);
  const [generatingHistory, setGeneratingHistory] = useState(false);

  // Carregar dispositivos
  const loadDevices = async () => {
    try {
      const response = await fetch(`${API_URL}/api/elevadores/devices`);
      const data = await response.json();
      setDevices(data || []);
    } catch (error) {
      console.error('Erro ao carregar dispositivos:', error);
    }
  };

  // Carregar registos disponíveis
  const loadAvailableRegisters = async () => {
    try {
      const response = await fetch(`${API_URL}/api/elevadores/registos-disponiveis`);
      const data = await response.json();
      setAvailableRegisters(data.registos || []);
      setAutomatoTipos(data.automato_tipos || []);
      // Guardar registos separados por secção
      setRegistosEntradas(data.registos_entradas || []);
      setRegistosSaidas(data.registos_saidas || []);
      setRegistosEstado(data.registos_estado || []);
    } catch (error) {
      console.error('Erro ao carregar registos:', error);
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadDevices(), loadAvailableRegisters()]);
      setLoading(false);
    };
    init();
  }, []);

  // Guardar dispositivo
  const handleSaveDevice = async () => {
    if (!deviceForm.local) {
      toast.error('Preencha o local');
      return;
    }

    try {
      const url = editingDevice 
        ? `${API_URL}/api/elevadores/devices/${editingDevice.id}`
        : `${API_URL}/api/elevadores/devices`;
      
      const method = editingDevice ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(deviceForm)
      });
      const data = await response.json();

      if (data.success) {
        toast.success(editingDevice ? 'Elevador atualizado!' : 'Elevador adicionado!');
        setShowDeviceDialog(false);
        resetDeviceForm();
        loadDevices();
        
        // Se for novo dispositivo, gerar histórico
        if (!editingDevice && data.device?.id) {
          toast.info('A gerar histórico simulado...');
          await generateHistory(data.device.id);
        }
      } else {
        toast.error(data.detail || 'Erro ao guardar');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    }
  };

  // Gerar histórico
  const generateHistory = async (elevatorId) => {
    setGeneratingHistory(true);
    try {
      const response = await fetch(`${API_URL}/api/elevadores/gerar-historico/${elevatorId}?horas=24`, {
        method: 'POST'
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success(`${data.message}`);
      }
    } catch (error) {
      toast.error('Erro ao gerar histórico');
    }
    setGeneratingHistory(false);
  };

  // Remover dispositivo
  const handleDeleteDevice = async (deviceId) => {
    if (!window.confirm('Tem a certeza que deseja remover este elevador?')) return;

    try {
      const response = await fetch(`${API_URL}/api/elevadores/devices/${deviceId}`, {
        method: 'DELETE'
      });
      const data = await response.json();

      if (data.success) {
        toast.success('Elevador removido!');
        loadDevices();
      } else {
        toast.error(data.detail || 'Erro ao remover');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    }
  };

  // Editar dispositivo
  const handleEditDevice = (device) => {
    setEditingDevice(device);
    setDeviceForm({
      local: device.local,
      descricao: device.descricao || '',
      equipamento_id: device.equipamento_id || '',
      automato_tipo: device.automato_tipo || 'schneider_tm221',
      automato_ip: device.automato_ip || '',
      automato_port: device.automato_port || 502,
      unit_id: device.unit_id || 1,
      registos_config: device.registos_config || {}
    });
    setShowDeviceDialog(true);
  };

  const resetDeviceForm = () => {
    setDeviceForm({
      local: '',
      descricao: '',
      equipamento_id: '',
      automato_tipo: 'schneider_tm221',
      automato_ip: '',
      automato_port: 502,
      unit_id: 1,
      registos_config: {}
    });
    setEditingDevice(null);
  };

  // Agrupar registos por categoria
  const registersByCategory = availableRegisters.reduce((acc, reg) => {
    const cat = reg.category || 'outros';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(reg);
    return acc;
  }, {});

  const categoryLabels = {
    status: 'Estado',
    alarm: 'Alarmes',
    sensor: 'Sensores',
    maintenance: 'Manutenção',
    electrical: 'Elétrico',
    command: 'Comando',
    outros: 'Outros'
  };

  const categoryColors = {
    status: 'bg-blue-100 text-blue-800',
    alarm: 'bg-red-100 text-red-800',
    sensor: 'bg-green-100 text-green-800',
    maintenance: 'bg-yellow-100 text-yellow-800',
    electrical: 'bg-purple-100 text-purple-800',
    command: 'bg-orange-100 text-orange-800',
    outros: 'bg-gray-100 text-gray-800'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/elevadores')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <MoveVertical className="h-6 w-6 text-purple-500" />
              Configuração Elevadores
            </h1>
            <p className="text-gray-500">Gerir elevadores e autómatos Schneider TM221</p>
          </div>
        </div>
        <Badge className="bg-purple-100 text-purple-800">
          {devices.length} Elevadores Configurados
        </Badge>
      </div>

      <Tabs defaultValue="devices" className="space-y-4">
        <TabsList>
          <TabsTrigger value="devices" className="flex items-center gap-2">
            <MoveVertical className="h-4 w-4" />
            Elevadores ({devices.length})
          </TabsTrigger>
          <TabsTrigger value="registos" className="flex items-center gap-2">
            <Database className="h-4 w-4" />
            Registos Disponíveis
          </TabsTrigger>
        </TabsList>

        {/* Tab: Dispositivos */}
        <TabsContent value="devices" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Elevadores / Autómatos</CardTitle>
                <CardDescription>Lista de elevadores configurados com autómatos Schneider</CardDescription>
              </div>
              <Dialog open={showDeviceDialog} onOpenChange={setShowDeviceDialog}>
                <DialogTrigger asChild>
                  <Button onClick={() => { resetDeviceForm(); setShowDeviceDialog(true); }}>
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar Elevador
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>
                      {editingDevice ? 'Editar Elevador' : 'Novo Elevador / Autómato'}
                    </DialogTitle>
                    <DialogDescription>
                      Configure os detalhes do elevador e a conexão com o autómato
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-4 py-4">
                    {/* Informação básica */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Local *</Label>
                        <Input 
                          placeholder="Ex: Campanhã (Teste)"
                          value={deviceForm.local}
                          onChange={(e) => setDeviceForm(prev => ({ ...prev, local: e.target.value }))}
                          data-testid="input-local"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Descrição</Label>
                        <Input 
                          placeholder="Ex: Elevador Principal"
                          value={deviceForm.descricao}
                          onChange={(e) => setDeviceForm(prev => ({ ...prev, descricao: e.target.value }))}
                          data-testid="input-descricao"
                        />
                      </div>
                    </div>

                    {/* ID do Equipamento Raspberry */}
                    <div className="border rounded-lg p-4 space-y-3 bg-blue-50 border-blue-200">
                      <h4 className="font-medium flex items-center gap-2 text-blue-800">
                        <Network className="h-4 w-4" />
                        Integração Raspberry Pi
                      </h4>
                      <div className="space-y-2">
                        <Label>ID do Equipamento</Label>
                        <Input 
                          placeholder="Ex: PLC1_ELEVADOR_CAMPANHA"
                          value={deviceForm.equipamento_id}
                          onChange={(e) => setDeviceForm(prev => ({ ...prev, equipamento_id: e.target.value }))}
                          data-testid="input-equipamento-id"
                        />
                        <p className="text-xs text-blue-600">
                          Identificador enviado pelo Raspberry no campo "equipamento". Necessário para receber dados em tempo real.
                        </p>
                      </div>
                    </div>

                    {/* Configuração do Autómato */}
                    <div className="border rounded-lg p-4 space-y-4 bg-gray-50">
                      <h4 className="font-medium flex items-center gap-2">
                        <Cpu className="h-4 w-4" />
                        Configuração do Autómato
                      </h4>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Tipo de Autómato</Label>
                          <Select 
                            value={deviceForm.automato_tipo} 
                            onValueChange={(v) => setDeviceForm(prev => ({ ...prev, automato_tipo: v }))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {automatoTipos.map(tipo => (
                                <SelectItem key={tipo.id} value={tipo.id}>
                                  {tipo.nome}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Endereço IP</Label>
                          <Input 
                            placeholder="192.168.1.100 (opcional)"
                            value={deviceForm.automato_ip}
                            onChange={(e) => setDeviceForm(prev => ({ ...prev, automato_ip: e.target.value }))}
                          />
                          <p className="text-xs text-gray-500">Deixe vazio para usar dados simulados</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Porta Modbus</Label>
                          <Input 
                            type="number"
                            value={deviceForm.automato_port}
                            onChange={(e) => setDeviceForm(prev => ({ ...prev, automato_port: parseInt(e.target.value) || 502 }))}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Unit ID</Label>
                          <Input 
                            type="number"
                            value={deviceForm.unit_id}
                            onChange={(e) => setDeviceForm(prev => ({ ...prev, unit_id: parseInt(e.target.value) || 1 }))}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Nota sobre dados simulados */}
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-sm text-yellow-800">
                        <strong>Nota:</strong> Se não configurar o IP do autómato, serão utilizados dados simulados. 
                        O histórico será gerado automaticamente ao criar o elevador.
                      </p>
                    </div>
                  </div>

                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowDeviceDialog(false)}>
                      Cancelar
                    </Button>
                    <Button onClick={handleSaveDevice}>
                      <Save className="h-4 w-4 mr-2" />
                      {editingDevice ? 'Atualizar' : 'Adicionar'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {devices.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <MoveVertical className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p className="font-medium">Nenhum elevador configurado</p>
                  <p className="text-sm">Adicione elevadores para começar a monitorizar</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Local</TableHead>
                      <TableHead>Descrição</TableHead>
                      <TableHead>Autómato</TableHead>
                      <TableHead>Integração</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {devices.map((device) => (
                      <TableRow key={device.id} data-testid={`device-row-${device.id}`}>
                        <TableCell className="font-medium">{device.local}</TableCell>
                        <TableCell>{device.descricao || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="gap-1">
                            <Cpu className="h-3 w-3" />
                            {automatoTipos.find(t => t.id === device.automato_tipo)?.nome || device.automato_tipo}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {device.equipamento_id ? (
                            <div className="flex flex-col gap-1">
                              <Badge className="bg-green-100 text-green-700 gap-1">
                                <Network className="h-3 w-3" /> Raspberry Pi
                              </Badge>
                              <span className="text-xs text-gray-500 font-mono">{device.equipamento_id}</span>
                            </div>
                          ) : device.automato_ip ? (
                            <Badge variant="outline" className="gap-1">
                              <Network className="h-3 w-3" /> {device.automato_ip}:{device.automato_port}
                            </Badge>
                          ) : (
                            <Badge className="bg-yellow-100 text-yellow-700">
                              Simulado
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {device.ativo ? (
                            <Badge className="bg-green-100 text-green-700">
                              <CheckCircle className="h-3 w-3 mr-1" /> Ativo
                            </Badge>
                          ) : (
                            <Badge className="bg-gray-100 text-gray-600">
                              <XCircle className="h-3 w-3 mr-1" /> Inativo
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => generateHistory(device.id)}
                              disabled={generatingHistory}
                              title="Gerar mais histórico"
                            >
                              <History className={`h-4 w-4 ${generatingHistory ? 'animate-spin' : ''}`} />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleEditDevice(device)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="text-red-500 hover:text-red-700"
                              onClick={() => handleDeleteDevice(device.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Registos */}
        <TabsContent value="registos" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Registos do Autómato Schneider TM221CE40T
              </CardTitle>
              <CardDescription>
                Registos organizados por secção do payload (Entradas, Saídas, Estado)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Accordion type="multiple" defaultValue={["entradas", "estado"]} className="w-full">
                {/* Entradas */}
                <AccordionItem value="entradas">
                  <AccordionTrigger>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-blue-100 text-blue-800">Entradas</Badge>
                      <span className="text-sm text-gray-500">({registosEntradas.length} registos)</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <RegisterTable registers={registosEntradas} categoryLabels={categoryLabels} categoryColors={categoryColors} />
                  </AccordionContent>
                </AccordionItem>

                {/* Saídas */}
                <AccordionItem value="saidas">
                  <AccordionTrigger>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-orange-100 text-orange-800">Saídas</Badge>
                      <span className="text-sm text-gray-500">({registosSaidas.length} registos)</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <RegisterTable registers={registosSaidas} categoryLabels={categoryLabels} categoryColors={categoryColors} />
                  </AccordionContent>
                </AccordionItem>

                {/* Estado */}
                <AccordionItem value="estado">
                  <AccordionTrigger>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-green-100 text-green-800">Estado</Badge>
                      <span className="text-sm text-gray-500">({registosEstado.length} registos)</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <RegisterTable registers={registosEstado} categoryLabels={categoryLabels} categoryColors={categoryColors} />
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ConfiguracaoElevadores;

// Sub-componente para tabela de registos
const RegisterTable = ({ registers, categoryLabels, categoryColors }) => (
  <Table>
    <TableHeader>
      <TableRow>
        <TableHead>Nome do Registo</TableHead>
        <TableHead>Descrição</TableHead>
        <TableHead>Categoria</TableHead>
        <TableHead>Tipo</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {registers.map((reg) => (
        <TableRow key={reg.name}>
          <TableCell className="font-mono text-sm">{reg.name}</TableCell>
          <TableCell>{reg.label}</TableCell>
          <TableCell>
            <Badge className={categoryColors[reg.category] || 'bg-gray-100 text-gray-800'}>
              {categoryLabels[reg.category] || reg.category}
            </Badge>
          </TableCell>
          <TableCell>
            <Badge variant="outline">
              {reg.type === 'bool' ? 'Boolean' : reg.type}
            </Badge>
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);
